import React, { useState } from 'react';
import { Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import CustomModal from 'components/CustomModal';
import { BankingService } from 'services';
import { useSnackbar } from 'notistack';

const bankingService = new BankingService();

type Props = {
  show: boolean;
  onClose: () => void;
  onConfirm: (otp: string) => void;
};

const OtpModal = ({ show, onClose, onConfirm }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const startCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setSending(true);
    try {
      const response = await bankingService.requestOtp();
      setCodeSent(true);
      startCooldown();
      if (response.code) {
        enqueueSnackbar(`Código OTP (dev): ${response.code}`, { variant: 'info', autoHideDuration: 30000 });
      } else {
        enqueueSnackbar('Código enviado para o seu email.', { variant: 'success' });
      }
    } catch {
      enqueueSnackbar('Erro ao enviar código. Tente novamente.', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = () => {
    if (!otp.trim()) {
      enqueueSnackbar('Informe o código recebido por email.', { variant: 'warning' });
      return;
    }
    onConfirm(otp.trim());
  };

  const handleClose = () => {
    setCodeSent(false);
    setOtp('');
    setResendCooldown(0);
    onClose();
  };

  return (
    <CustomModal
      open={show}
      onClose={handleClose}
      sx={{
        p: '30px',
        minWidth: '500px',
      }}
    >
      <Grid container>
        <Grid item xs={12} mb={3}>
          <Typography align="left" fontWeight="medium" fontSize="20px">
            Verificação necessária
          </Typography>
          <Typography align="left" fontWeight="medium" fontSize="15px" color="#5D5D5D" mt={1}>
            {codeSent
              ? 'Insira o código de 6 dígitos enviado para o seu email.'
              : 'Para continuar, enviaremos um código de verificação para o seu email cadastrado.'}
          </Typography>
        </Grid>

        {codeSent && (
          <Grid item xs={12} mb={2}>
            <TextField
              label="Código de verificação"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              fullWidth
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
            />
            <Button
              variant="text"
              size="small"
              onClick={handleSendCode}
              disabled={sending || resendCooldown > 0}
              sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
            >
              {resendCooldown > 0 ? `Reenviar código (${resendCooldown}s)` : 'Não recebi o código — reenviar'}
            </Button>
          </Grid>
        )}

        <Grid
          item
          xs={12}
          mt={2}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            sx={{
              borderRadius: '9999px',
              border: '2px solid',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 4,
              py: 1,
            }}
          >
            CANCELAR
          </Button>

          {!codeSent ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendCode}
              disabled={sending}
              sx={{
                borderRadius: '9999px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                px: 4,
                py: 1,
              }}
            >
              {sending ? <CircularProgress size={20} color="inherit" /> : 'ENVIAR CÓDIGO'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={otp.length !== 6}
              sx={{
                borderRadius: '9999px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                px: 4,
                py: 1,
              }}
            >
              CONFIRMAR
            </Button>
          )}
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default OtpModal;
